import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { RouterLink } from '@angular/router';

export interface BreadcrumbItem {
    label: string;
    link?: string;
}

@Component({
    selector: 'app-breadcrumb',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterLink],
    styles: `
        @use "variables" as *;
        @use "functions" as *;

        :host {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin: 0 0 25px;
            gap: 1rem;

            @include break(768px) {
                margin: 0 0 15px;
            }
        }

        .breadcrumb-left {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            flex-wrap: nowrap;
            overflow: hidden;
            min-width: 0;

            .breadcrumb-item {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                font-size: 0.875rem;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.02em;
                min-width: 0;
                flex-shrink: 1;

                a, .current {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 200px;
                }

                a {
                    color: $dark-blue;
                    text-decoration: none;
                    transition: color 0.2s ease;

                    &:hover {
                        color: $red;
                    }
                }

                .current {
                    color: $dark-blue;
                }

                .separator {
                    display: flex;
                    align-items: center;
                    width: 7px;
                    height: 12px;
                    flex-shrink: 0;

                    svg {
                        width: 100%;
                        height: 100%;
                    }
                }
            }

            @include break(768px) {
                font-size: 0.75rem;

                .breadcrumb-item a, .breadcrumb-item .current {
                    max-width: 120px;
                }
            }
        }

        .breadcrumb-right {
            font-size: 0.875rem;
            font-weight: 600;
            color: $dark-blue;
            text-transform: uppercase;
            letter-spacing: 0.02em;
            white-space: nowrap;

            @include break(768px) {
                font-size: 0.75rem;
            }
        }
    `,
    template: `
        <div class="breadcrumb-left">
            @for (item of breadcrumbItems; track $index; let isLast = $last) {
                <div class="breadcrumb-item">
                    @if (item.link && !isLast) {
                        <a [routerLink]="item.link">{{ item.label }}</a>
                    } @else {
                        <span class="current">{{ item.label }}</span>
                    }
                    @if (!isLast) {
                        <span class="separator">
                            <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6.05331 6.05331C6.3462 5.76042 6.3462 5.28554 6.05331 4.99265L1.28034 0.219679C0.987446 -0.0732145 0.512572 -0.0732145 0.219679 0.219679C-0.0732145 0.512572 -0.0732145 0.987446 0.219679 1.28034L4.46232 5.52298L0.219679 9.76562C-0.0732145 10.0585 -0.0732145 10.5334 0.219679 10.8263C0.512572 11.1192 0.987446 11.1192 1.28034 10.8263L6.05331 6.05331ZM4.52298 5.52298V6.27298H5.52298V5.52298V4.77298H4.52298V5.52298Z" fill="#0C0C3A"/>
                            </svg>
                        </span>
                    }
                </div>
            }
        </div>
        
        @if (rightText) {
            <div class="breadcrumb-right">
                {{ rightText }}
            </div>
        }
    `
})
export class BreadcrumbComponent {
    @Input({ required: true }) breadcrumbItems!: BreadcrumbItem[];
    @Input() rightText?: string;
}
